import {
  HttpClient,
  HttpError,
  type HttpRequest,
  type HttpResponse,
  type ILogger,
  LogLevel,
} from '@microsoft/signalr';

/**
 * Retry policy, quiet logging and status tracking for the Job Ops live connection (`/hubs/job-chat`).
 *
 * VE-SIGNALR: the old client retried every 15 s forever whatever the error (500, network or 401), printed 3-4 red
 * console lines per attempt (including the full WebSocket URL with the admin JWT in `access_token=`), and kept
 * looping after the 4 h admin token expired. Everything that decides *whether* and *when* to retry lives here as
 * pure functions so it can be unit-tested without a browser or a server.
 */

/** Waits before attempt n+1 (n = failed attempts so far in this outage); after the list, every 60 s. */
export const LIVE_RETRY_SCHEDULE_MS: readonly number[] = [0, 2_000, 5_000, 10_000, 20_000, 30_000];
export const LIVE_RETRY_STEADY_MS = 60_000;
/** +-20 % so many tabs that dropped together (a Job Ops deploy) do not all reconnect in the same 70 ms. */
export const LIVE_RETRY_JITTER = 0.2;

/** Client timeouts, paired with Job Ops `AddSignalR` (ClientTimeoutInterval 60 s, KeepAliveInterval 15 s). */
export const LIVE_SERVER_TIMEOUT_MS = 60_000;
export const LIVE_KEEP_ALIVE_MS = 15_000;

/** Delay before the next attempt without jitter. */
export function baseRetryDelayMs(failedAttempts: number): number {
  const n = Math.max(0, Math.floor(failedAttempts));
  return n < LIVE_RETRY_SCHEDULE_MS.length ? LIVE_RETRY_SCHEDULE_MS[n] : LIVE_RETRY_STEADY_MS;
}

/** Delay before the next attempt with +-20 % jitter (`random` in [0, 1), injectable for tests). 0 stays 0. */
export function retryDelayMs(failedAttempts: number, random: () => number = Math.random): number {
  const base = baseRetryDelayMs(failedAttempts);
  if (base === 0) return 0;
  const r = Math.min(Math.max(random(), 0), 1);
  return Math.round(base * (1 - LIVE_RETRY_JITTER + 2 * LIVE_RETRY_JITTER * r));
}

/**
 * Status code of a failed attempt. SignalR wraps a negotiate HttpError in FailedToNegotiateWithServerError and keeps
 * only its text ("... Status code '401'"), so the status the tracking HTTP client saw is preferred; the error itself
 * (statusCode, inner errors, message) is the fallback.
 */
export function statusFromError(error: unknown, seenStatus: number | null = null): number | null {
  if (seenStatus) return seenStatus;
  if (!error || typeof error !== 'object') return null;
  if (error instanceof HttpError) return error.statusCode;
  const o = error as { statusCode?: unknown; innerErrors?: unknown; message?: unknown };
  if (typeof o.statusCode === 'number') return o.statusCode;
  if (Array.isArray(o.innerErrors)) {
    for (const inner of o.innerErrors) {
      const s = statusFromError(inner);
      if (s) return s;
    }
  }
  const m = typeof o.message === 'string' ? /Status code '(\d{3})'/.exec(o.message) : null;
  return m ? Number(m[1]) : null;
}

export type RetryDecision =
  | { kind: 'retry'; delayMs: number }
  /** 401 or the stored token is expired: no retries until a fresh token appears. */
  | { kind: 'session-expired' }
  /** The server refused the connection for good (HTTP 4xx other than 401/408/429). */
  | { kind: 'refused'; status: number }
  /** No timer while the browser is offline; the `online` event resumes. */
  | { kind: 'offline' }
  /** No timer while the tab is hidden; `visibilitychange` back to visible resumes. */
  | { kind: 'hidden' };

export interface RetryInput {
  /** Failed attempts since the last good connection (0 right after a live connection drops). */
  failedAttempts: number;
  /** HTTP status of the failure, when there was one. */
  status: number | null;
  /** A token is present and its `exp` is in the future. */
  tokenLive: boolean;
  visible: boolean;
  online: boolean;
  random?: () => number;
}

/** The one place that decides what happens after a drop or a failed attempt. */
export function decideRetry(input: RetryInput): RetryDecision {
  if (input.status === 401 || !input.tokenLive) return { kind: 'session-expired' };
  const s = input.status;
  if (s !== null && s >= 400 && s < 500 && s !== 408 && s !== 429) return { kind: 'refused', status: s };
  if (!input.online) return { kind: 'offline' };
  if (!input.visible) return { kind: 'hidden' };
  return { kind: 'retry', delayMs: retryDelayMs(input.failedAttempts, input.random) };
}

/** Removes anything that could carry the admin JWT: query strings of URLs, `access_token=`, bearer values, JWTs. */
export function redactLiveLogText(text: string): string {
  return text
    .replace(/\b(?:https?|wss?):\/\/[^\s'"`<>)]+/gi, (url) => {
      const cut = url.search(/[?#]/);
      return cut >= 0 ? url.slice(0, cut) : url;
    })
    .replace(/access_token=[^&\s'"`]*/gi, 'access_token=[redacted]')
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [redacted]')
    .replace(/eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, '[redacted-token]');
}

/**
 * SignalR messages about the connection going down or failing to come up. They are expected during a Job Ops
 * restart or a network blip and are replaced by the service's own single "disconnected, retrying" line.
 */
const CONNECTION_NOISE: readonly RegExp[] = [
  /^Failed to complete negotiation with the server/,
  /^Failed to start the connection/,
  /^Failed to start the transport/,
  /^Failed to start the HttpConnection before stop\(\) was called/,
  /^Connection disconnected with error/,
  /^Error from HTTP request/,
  /^Timeout from HTTP request/,
  /^Call to HttpConnection\.stopConnection\(.*\) was ignored/,
  /^The page is being frozen/,
  /^\(WebSockets transport\)/,
  /^\(SSE transport\)/,
];

export function isConnectionNoise(message: string): boolean {
  return CONNECTION_NOISE.some((re) => re.test(message));
}

type ConsoleSink = Pick<Console, 'warn' | 'error'>;

/**
 * Hub logger: drops everything below Warning (that is where SignalR prints "WebSocket connected to wss://...
 * access_token=..."), drops the expected connection-failure noise, and prints any other warning or error once,
 * redacted, so real problems (a client callback throwing, a handshake or protocol error) stay visible.
 */
export class LiveConnectionLogger implements ILogger {
  constructor(private readonly sink: ConsoleSink = console) {}

  log(logLevel: LogLevel, message: string): void {
    if (logLevel < LogLevel.Warning || logLevel === LogLevel.None) return;
    const text = String(message ?? '');
    if (isConnectionNoise(text)) return;
    const line = `[live] ${redactLiveLogText(text)}`;
    if (logLevel >= LogLevel.Error) this.sink.error(line);
    else this.sink.warn(line);
  }
}

/**
 * Wraps SignalR's default HTTP client to remember the status of the last failed request (the negotiate call), so
 * a 401 can be told apart from a 500 or a network failure even though SignalR only keeps the error text.
 */
export class StatusTrackingHttpClient extends HttpClient {
  constructor(
    private readonly inner: HttpClient,
    private readonly onStatus: (status: number) => void,
  ) {
    super();
  }

  override async send(request: HttpRequest): Promise<HttpResponse> {
    try {
      const response = await this.inner.send(request);
      if (response.statusCode >= 400) this.onStatus(response.statusCode);
      return response;
    } catch (e) {
      if (e instanceof HttpError) this.onStatus(e.statusCode);
      throw e;
    }
  }

  override getCookieString(url: string): string {
    return this.inner.getCookieString(url);
  }
}
